import Container from '@mui/material/Container';
import { useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AppFooter } from '../../components/AppFooter/AppFooter';
import { PageURL } from '../../router/pageURL';
import { AgreementPage, CreateAccountPage } from './CreateAccountPage/CreateAccountPage';
import { CreateOrganisation } from './CreateOrganisation/CreateOrganisation';
import { NewSignupPageSectionEnum } from './enums';
import { SelectOrganisation } from './SelectOrganisation/SelectOrganisation';
import { config } from 'utils/config';
import { useAuth, useIsBrokerOnboardingNewFlowEnabled } from 'providers';
import { useBrokerOnboarding } from 'queries';

const SIGNUP_FLOW_SECTION_KEYS = new Set<string>([
  NewSignupPageSectionEnum.POST_SIGN_UP_PAGE,
  NewSignupPageSectionEnum.SELECT_ORGANISATION_PAGE,
  NewSignupPageSectionEnum.CREATE_ORGANISATION,
]);

export const SignupPageProd = () => {
  const [searchParams] = useSearchParams();
  const { currentSignUpStep, isLoaded, isSignedIn, updateUser } = useAuth();
  const isBrokerOnboardingNewFlowEnabled = useIsBrokerOnboardingNewFlowEnabled();
  const { data: brokerOnboarding, isFetched: isBrokerOnboardingFetched } = useBrokerOnboarding();
  const navigate = useNavigate();
  const formSection = searchParams.get('section') as NewSignupPageSectionEnum;
  const rawSection = searchParams.get('section');
  const hasValidFlowSection = Boolean(rawSection && SIGNUP_FLOW_SECTION_KEYS.has(rawSection));

  useEffect(() => {
    if (!isLoaded) {
      return;
    }

    if (isBrokerOnboardingNewFlowEnabled) {
      if (!isSignedIn) {
        return;
      }

      if (!isBrokerOnboardingFetched) {
        return;
      }

      const targetSection = brokerOnboarding?.section ?? NewSignupPageSectionEnum.POST_SIGN_UP_PAGE;

      if (targetSection === NewSignupPageSectionEnum.COMPLETED) {
        navigate(PageURL.ROOT);
        return;
      }

      if (rawSection === targetSection) {
        return;
      }

      navigate(`${PageURL.SIGNUP}?section=${targetSection}`, { replace: true });
      return;
    }

    if (currentSignUpStep === NewSignupPageSectionEnum.COMPLETED) {
      navigate(PageURL.ROOT);
      return;
    }

    if (!isSignedIn) {
      return;
    }

    if (hasValidFlowSection) {
      return;
    }

    const targetSection = currentSignUpStep ?? NewSignupPageSectionEnum.POST_SIGN_UP_PAGE;

    navigate(`${PageURL.SIGNUP}?section=${targetSection}`, { replace: true });
  }, [
    brokerOnboarding?.section,
    currentSignUpStep,
    hasValidFlowSection,
    isBrokerOnboardingFetched,
    isBrokerOnboardingNewFlowEnabled,
    isLoaded,
    isSignedIn,
    navigate,
    rawSection,
  ]);

  const postSignupMetadataSyncedRef = useRef(false);

  useEffect(() => {
    postSignupMetadataSyncedRef.current = false;
  }, [isSignedIn]);

  useEffect(() => {
    if (isBrokerOnboardingNewFlowEnabled) {
      return;
    }

    if (!isLoaded || !isSignedIn || currentSignUpStep) {
      return;
    }

    if (formSection !== NewSignupPageSectionEnum.POST_SIGN_UP_PAGE) {
      return;
    }

    if (postSignupMetadataSyncedRef.current) {
      return;
    }

    postSignupMetadataSyncedRef.current = true;

    updateUser?.({
      unsafeMetadata: {
        currentSignUpStep: NewSignupPageSectionEnum.POST_SIGN_UP_PAGE,
      },
    });
  }, [currentSignUpStep, formSection, isBrokerOnboardingNewFlowEnabled, isLoaded, isSignedIn, updateUser]);

  const content = useMemo(() => {
    switch (formSection) {
      case NewSignupPageSectionEnum.POST_SIGN_UP_PAGE:
        return <AgreementPage />;
      case NewSignupPageSectionEnum.SELECT_ORGANISATION_PAGE:
        return <SelectOrganisation />;
      case NewSignupPageSectionEnum.CREATE_ORGANISATION:
        return <CreateOrganisation />;
      default:
        return isSignedIn ? null : <CreateAccountPage />;
    }
  }, [formSection, isSignedIn]);

  return (
    <div className='min-h-screen grid grid-rows-[1fr_auto]'>
      <main>
        <Container component='div' className={`flex h-full flex-col justify-center`}>
          <div className={`flex justify-center`}>{content}</div>
        </Container>
      </main>
      <AppFooter />
    </div>
  );
};

export const SignupPage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    if (config.isLocalAuth) {
      navigate(PageURL.ROOT);
    }
  }, [navigate]);

  if (config.isLocalAuth) {
    return (
      <div className='flex flex-col items-center justify-center p-8 text-gray-600'>
        <p>Local dev: you are signed in. Set VITE_USE_LOCAL_AUTH=false to use Clerk login.</p>
      </div>
    );
  }

  return <SignupPageProd />;
};
